import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IUser,
  IUserImportMeta,
  UserImportErrorTypeSource,
  UserImportErrorTypeUser,
  UserImportStatus
} from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { keyBy, sortBy } from 'lodash';
import { ChevronLeft, Import } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { DataGrid, DataGridItem } from '../../../../components/DataGrid';
import { LinkExternal } from '../../../../components/LinkExternal';
import { Loader, PageLoader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section, SubSection } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { joinWithCommaAndAnd } from '../../../../services/enumerate';
import { removeDoc } from '../../../../services/firebase';
import { LightMdToJsx } from '../../../../services/formatter';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { formatDatePrecise, toMoment } from '../../../../services/time';
import {
  commitImport,
  getUserImportDataSourceName,
  getUserImportSourceNameFromIdentifier,
  useImportById,
  useImportDiffset,
  useImportsByOrgId,
  useUserImportDataSources
} from '../../../../services/user/import';
import { useTheme } from '../../../../themes';
import { useAnchorFields } from '../../../Experiences/components/Anchors';
import { DiffSetRenderer } from './components/DiffSetRenderer';
import { Effects } from './components/Effects';
import { ImportSourceChip } from './components/ImportSourceChip';
import { ImportStatusChip } from './components/ImportStatusChip';

// split out component as it loads some additional data - no
// need to do that if there are no errors
const ImportErrorListInner = ({ d }: { d: Doc<IUserImportMeta> }) => {
  const { t } = useUnsafeTranslation();
  const [sources, loading] = useUserImportDataSources(d.data.orgId);
  const { errors } = d.data;
  const sourcesById = useMemo(
    () => (sources ? keyBy(sources, (s) => s.id) : {}),
    [sources]
  );

  if (loading) {
    return null;
  }

  return (
    <FlexContainer
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      {errors.map((error, i) => {
        const fullSource = sourcesById[error.source.id];
        const sourceTypeName = getUserImportSourceNameFromIdentifier(
          error.source
        );
        const sourceName = fullSource
          ? getUserImportDataSourceName(fullSource)
          : sourceTypeName;
        if (error.type === UserImportErrorTypeSource.UNKNOWN_SOURCE) {
          return (
            <Alert severity="error" key={i}>
              {t(`There is no active integration with Dado and {{ source }}.`, {
                source: sourceTypeName
              })}
            </Alert>
          );
        }
        if (
          error.type === UserImportErrorTypeSource.EXTERNAL_SOURCE_NOT_FOUND
        ) {
          return (
            <Alert severity="error" key={i}>
              <LightMdToJsx>
                {t(
                  `Dado cannot find import source for **{{ source }}**. It might have been deleted or Dado's access revoked.`,
                  {
                    source: sourceName
                  }
                )}
              </LightMdToJsx>
            </Alert>
          );
        }

        if (error.type === UserImportErrorTypeSource.INTEGRATION_ERROR) {
          return (
            <Alert severity="error" key={i}>
              {t(
                `There is no active integration between Dado and {{ source }}. Please (re)add this integration and retry your import.`,
                {
                  source: sourceTypeName
                }
              )}
            </Alert>
          );
        }

        if (error.type === UserImportErrorTypeSource.MISSING_FIELDS) {
          return (
            <Alert severity="error" key={i}>
              <LightMdToJsx>
                {t('import.errors.source.missingFields', {
                  count: error.data?.fields?.length || 0,
                  source: sourceName,
                  fields: joinWithCommaAndAnd(
                    error.data?.fields.map((f: string) => `'${f}'`) || [],
                    t
                  )
                })}
              </LightMdToJsx>
            </Alert>
          );
        }

        // new-main-hpz: DUPLICATE_HEADERS was added to UserImportErrorTypeSource
        // in new-main commit a3d2e0828e. The installed @dadohr/shared@1.9.0
        // doesn't have the enum value yet, so we compare against the string
        // literal. Once shared publishes a version that includes
        // DUPLICATE_HEADERS, drop the cast and use the enum directly.
        if (
          error.type ===
          ('DUPLICATE_HEADERS' as UserImportErrorTypeSource)
        ) {
          const duplicates: string[] = error.data?.duplicates || [];
          const blankColumnLetters: string[] =
            error.data?.blankColumnLetters || [];
          const dupList = joinWithCommaAndAnd(
            duplicates.map((h) => `'${h}'`),
            t
          );
          const blankList = joinWithCommaAndAnd(
            blankColumnLetters.map((c) => `'${c}'`),
            t
          );
          return (
            <Alert severity="error" key={i}>
              <LightMdToJsx>
                {duplicates.length && blankColumnLetters.length
                  ? t(
                      `**{{ source }}** has duplicate column headers ({{ duplicates }}) and blank header(s) in column(s) {{ blanks }}. Rename or remove the duplicates and add a header to (or delete) the blank columns, then retry the import.`,
                      {
                        source: sourceName,
                        duplicates: dupList,
                        blanks: blankList
                      }
                    )
                  : duplicates.length
                    ? t(
                        `**{{ source }}** has duplicate column headers: {{ duplicates }}. Rename or remove the duplicate columns and retry the import.`,
                        {
                          source: sourceName,
                          duplicates: dupList
                        }
                      )
                    : t(
                        `**{{ source }}** has blank header(s) in column(s) {{ blanks }}. Add a header to each column or delete it, then retry the import.`,
                        {
                          source: sourceName,
                          blanks: blankList
                        }
                      )}
              </LightMdToJsx>
            </Alert>
          );
        }
        if (error.type === UserImportErrorTypeSource.UNEXPECTED_DATE_FORMAT) {
          console.log('ERROR', error);
          return (
            <Alert severity="error" key={i}>
              <LightMdToJsx>
                {t(
                  error?.data.value
                    ? 'Unexpected date format found in **{{ source }}**. We expected all dates to be formatted like {{ dateFormat }}, but encountered {{ value }}.'
                    : 'Unexpected date format found in **{{ source }}**. We expected all dates to be formatted like {{ dateFormat }}.',
                  {
                    source: sourceName,
                    dateFormat: error?.data.format,
                    value: error?.data.value
                  }
                )}
              </LightMdToJsx>
            </Alert>
          );
        }

        if (error.type === UserImportErrorTypeSource.INVALID_EXCEL_DATE) {
          return (
            <Alert severity="error" key={i}>
              <LightMdToJsx>
                {t(
                  'Unexpected date format found in **{{ source }}**. All cells containing dates should be formatted as date type, specifically {{ dateFormat }}.',
                  {
                    source: sourceName,
                    dateFormat: error?.data.format,
                    value: error?.data.value
                  }
                )}
              </LightMdToJsx>
            </Alert>
          );
        }

        // if (error.type === UserImportErrorTypeUser.ORG_NOT_FOUND) {
        //   return (
        //     <Alert severity="error">
        //       {t(`Organization not found! Please contact help@dadohr.com`)}
        //     </Alert>
        //   );
        // }
        return (
          <Alert severity="error" key={i}>
            {t(
              `Dado is having problems accessing the import source for {{ source }}. Please retry your import and, if the problems persist, contact `,
              {
                source: sourceTypeName
              }
            )}{' '}
            <LinkExternal href="mailto: help@dadohr.com">
              help@dadohr.com
            </LinkExternal>
            .
          </Alert>
        );
      })}
    </FlexContainer>
  );
};

const ImportErrorList = ({ d }: { d: Doc<IUserImportMeta> }) => {
  if (!d.data.errors.length) {
    return null;
  }
  return <ImportErrorListInner d={d} />;
};

export const FooterContainer = styled('div')`
  position: fixed;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.9);
  left: 0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100vw;
  gap: ${(p) => p.theme.spacing(2)}px;
  padding: ${(p) => p.theme.spacing(3)}px;
  padding-right: ${(p) => p.theme.spacing(3)}px;
`;

const Body = ({
  d,
  isNewest
}: {
  d: Doc<IUserImportMeta>;
  isNewest: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { org } = useCurrentUser();
  const anchorFields = useAnchorFields(org.customUserFields);
  const [diffSet, loading] = useImportDiffset(d);
  const { goTo, ROUTES } = useRoutes();

  const isOutdated = d.data.status === UserImportStatus.PREVIEW && !isNewest;

  const dataGridItems: DataGridItem[] = useMemo(() => {
    const hasResults =
      d.data.status !== UserImportStatus.PENDING &&
      d.data.status !== UserImportStatus.ERROR;
    return [
      {
        label: t('Status'),
        d: (
          <ImportStatusChip
            status={d.data.status}
            size="small"
            outdated={isOutdated}
          />
        )
      },
      {
        label: t('Sources'),
        d: (
          <FlexContainer>
            {d.data.sources.map((s) => (
              <ImportSourceChip identifier={s} size="small" />
            ))}
          </FlexContainer>
        )
      },
      {
        label: t('Done at'),
        d: d.data.doneAt ? formatDatePrecise(toMoment(d.data.doneAt)) : '-'
      },
      {
        label: t('Changed Users'),
        d: hasResults
          ? d.data.stats.added + d.data.stats.changed + d.data.stats.removed
          : '-'
      },
      {
        label: t('Problems'),
        // Roll commit-time rejections into the Problems count — they're
        // surfaced in the Problems tab via the displayDiffSet below, so
        // the stat must include them or the count won't match the tab.
        d: hasResults
          ? d.data.stats.issues + (d.data.commitErrors?.length ?? 0)
          : '-'
      },
      {
        label: t('Effects'),
        d: hasResults ? d.data.stats.effects || '-' : '-'
      }
    ];
  }, [t, d, isOutdated]);

  const { status } = d.data;

  // Fire-and-track apply: the backend stamps `committingAt` (a plain timestamp,
  // NOT a new status) at commit start. While it's fresh and the import is still
  // PREVIEW, an apply is in flight — render "Committing…" from the live import
  // doc instead of blocking the button on the (potentially very long)
  // commitImport onCall. Blocking on that call kept a spinner up for the whole
  // apply and, with the page's listeners re-rendering under the write storm,
  // could balloon tab memory until the browser crashed. Mirrors the backend's
  // 2h staleness window so a hard-crashed commit re-enables Apply.
  const committingAtMs = (
    d.data as { committingAt?: { toMillis?: () => number } }
  ).committingAt?.toMillis?.();
  const isApplying =
    status === UserImportStatus.PREVIEW &&
    committingAtMs != null &&
    Date.now() - committingAtMs < 2 * 60 * 60 * 1000;
  const hasChanges = !!(
    d.data.stats.added +
    d.data.stats.changed +
    d.data.stats.removed
  );

  const commitErrors = d.data.commitErrors ?? [];

  // For PARTIAL_FAILURE imports, fold each commit-time rejection into the
  // diffSet's `issues` array as a synthetic InvalidUser. The Problems tab
  // already knows how to render issues, so this keeps the failure surface
  // consistent with the "DONE with validation issues" UX rather than
  // having a separate red banner for an outcome that is otherwise a
  // success. Issue type is the string 'COMMIT_REJECTED' — handled in
  // IssuesTable.IssueDescription. errorData.value carries the raw mutation
  // error so the row can display the underlying reason.
  const displayDiffSet = useMemo(() => {
    if (!diffSet) return diffSet;
    if (commitErrors.length === 0) return diffSet;
    const userById = new Map<string, Doc<IUser>>();
    diffSet.added.forEach((u) => userById.set(u.id, u));
    diffSet.changed.forEach((c) => userById.set(c.aft.id, c.aft));
    diffSet.removed.forEach((u) => userById.set(u.id, u));
    const extra = commitErrors
      .map((e) => {
        const u = userById.get(e.userId);
        if (!u) return null;
        return {
          user: u,
          // Cast — 'COMMIT_REJECTED' will land in the shared enum next bump.
          issue: 'COMMIT_REJECTED' as unknown as UserImportErrorTypeUser,
          errorData: { field: 'commit', value: e.error }
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
    return { ...diffSet, issues: [...diffSet.issues, ...extra] };
  }, [diffSet, commitErrors]);

  return (
    <>
      <PageToolbar
        backButton={
          <Button
            startIcon={<ChevronLeft size={16} />}
            variant="text"
            color="primary"
            style={{ marginLeft: '-12px' }}
          >
            <Link to={ROUTES.users.imports.overview.url()}>
              {t('Back to Imports')}
            </Link>
          </Button>
        }
      >
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('User Import')}: {formatDatePrecise(toMoment(d.data.createdAt))}{' '}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>
      <Section spacing={4}>
        <SubSection spacing={4}>
          <DataGrid ds={dataGridItems} />
        </SubSection>
      </Section>
      {(status === UserImportStatus.PREVIEW ||
        status === UserImportStatus.DONE ||
        status === UserImportStatus.PARTIAL_FAILURE) && (
        <Section>
          {loading && <Loader height={400} />}
          {displayDiffSet && (
            <DiffSetRenderer
              d={displayDiffSet}
              customFields={org.customUserFields}
              dateFormat={org.dateFormats.MMDDYYYY}
            />
          )}
        </Section>
      )}
      {!loading && status === UserImportStatus.ERROR && (
        <Section>
          <ImportErrorList d={d} />
        </Section>
      )}
      {!loading &&
        (status === UserImportStatus.PREVIEW ||
          status === UserImportStatus.DONE ||
          status === UserImportStatus.PARTIAL_FAILURE) &&
        diffSet &&
        (hasChanges || d.data.stats.effects > 0) && (
          <Section>
            <Effects
              d={d}
              diffSet={diffSet}
              anchorFields={anchorFields}
              orgDateFormat={org.dateFormats.MMDDYYYY}
            />
          </Section>
        )}
      <FooterContainer>
        {status === UserImportStatus.PENDING && (
          <Alert severity="info" icon={<Loader size={20} />}>
            {t('import.statusMessage.processing')}
          </Alert>
        )}

        {status === UserImportStatus.PREVIEW && isApplying && (
          <Alert severity="info" icon={<Loader size={20} />}>
            {`${t('Committing')}...`}
          </Alert>
        )}

        {status === UserImportStatus.PREVIEW && !isApplying && (
          <>
            <Alert severity="warning" icon={<Import size={20} />}>
              {isOutdated
                ? t('import.statusMessage.outdated')
                : t('import.statusMessage.ready')}
            </Alert>
            <div>
              {!isOutdated && (
                <ButtonWithPromise
                  variant="contained"
                  color="primary"
                  pending={`${t('Committing')}...`}
                  onClick={() => {
                    // Fire-and-track: kick off the commit but do NOT hold the
                    // button on the (long) onCall — the backend stamps
                    // `committingAt`, the doc listener flips `isApplying`, and
                    // this footer swaps to the "Committing…" alert. The short
                    // bridge promise below only covers the snapshot round-trip;
                    // errors surface via the import doc's ERROR status. A stray
                    // double-click is harmless — the backend skips a fresh
                    // in-flight commit.
                    commitImport(d.data.orgId, d.id).catch(() => undefined);
                    return new Promise((resolve) => setTimeout(resolve, 2500));
                  }}
                  style={{ marginRight: theme.spacing(1) }}
                >
                  {t('import.actions.commit')}
                </ButtonWithPromise>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  goTo(ROUTES.users.imports.overview.url());
                  removeDoc(d);
                }}
              >
                {t('import.actions.delete')}
              </Button>
            </div>
          </>
        )}

        {status === UserImportStatus.ERROR && (
          <>
            <div />
            <Button
              variant="outlined"
              onClick={() => {
                goTo(ROUTES.users.imports.overview.url());
                removeDoc(d);
              }}
            >
              {t('import.actions.delete')}
            </Button>
          </>
        )}
      </FooterContainer>
    </>
  );
};

const ContentOrgUserImportsDetails = ({ importId }: { importId: string }) => {
  const { org } = useCurrentUser();
  const [d, loadingD] = useImportById(importId);
  const [otherImports, loadingOtherImports] = useImportsByOrgId(org.id);

  const loading = loadingD || loadingOtherImports;

  const isNewest = useMemo(() => {
    if (d && otherImports) {
      const sortedImports = sortBy(
        otherImports.filter((i) => i.data.status !== UserImportStatus.ERROR),
        (x) => -x.data.createdAt.toMillis()
      );
      return sortedImports[0]?.id === d.id;
    }
    return false;
  }, [d, otherImports]);

  return (
    <PageBody noTopPadding>
      {loading && <PageLoader />}
      {d && otherImports && <Body d={d} isNewest={isNewest} />}
    </PageBody>
  );
};

export const PageOrgUserImportsDetails = ({
  importId
}: {
  importId: string;
}) => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.imports.details)) {
    return <NoAccess />;
  }

  return <ContentOrgUserImportsDetails importId={importId} />;
};
